import { useEffect, useState } from 'react';
import { Button, Calibrated, Card, GlassPill, SpeakButton } from './primitives';
import {
  dossierCompletePct,
  loadDossier,
  saveDossier,
  type DossierNotes,
} from '../lib/playerDossier';
import './PlayerDossier.css';

/**
 * Multidisciplinary review block — 4-corner notes the coach holds on
 * a player. Web1's MultidisciplinaryReviewEditor port (Sprint 4.5c).
 *
 * Lean version: 4 textareas (one per corner), localStorage-backed via
 * playerDossier.ts, SpeakButton next to each saved note so the coach
 * can hear yesterday's notes on the touchline before adding to them.
 *
 * Hidden from NOVICE coaches via <Calibrated min="GRASSROOTS"> — for
 * the absolute beginner this is too much surface; their Brain chat
 * does the equivalent capture.
 */
interface PlayerDossierProps {
  playerId: string;
  playerName: string;
}

const CORNERS: Array<{
  key: keyof Pick<DossierNotes, 'technical_tactical' | 'physical' | 'psychological' | 'social'>;
  label: string;
  placeholder: string;
  tag: 'technical' | 'physical' | 'psych' | 'social';
}> = [
  {
    key: 'technical_tactical',
    label: 'Technical / Tactical',
    placeholder: '1v1 confidence is up. Still drops shoulder before passing under pressure.',
    tag: 'technical',
  },
  {
    key: 'physical',
    label: 'Physical',
    placeholder: 'Recovery sprints holding up across 60min. Bio-band: late developer.',
    tag: 'physical',
  },
  {
    key: 'psychological',
    label: 'Psychological',
    placeholder: 'Composure on the ball noticeably better since the recent goal.',
    tag: 'psych',
  },
  {
    key: 'social',
    label: 'Social',
    placeholder: 'Talks more in the warm-up. Stepping into a captain-track role.',
    tag: 'social',
  },
];

export function PlayerDossier({ playerId, playerName }: PlayerDossierProps) {
  const [notes, setNotes] = useState<DossierNotes>(() => loadDossier(playerId));
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);

  useEffect(() => {
    setNotes(loadDossier(playerId));
  }, [playerId]);

  const pct = dossierCompletePct(notes);

  const handleBlur = (key: keyof DossierNotes, value: string) => {
    // Dedup intentionally NOT comparing to `notes[key]` here — onChange
    // already synced state by the time onBlur fires, so notes[key] ===
    // value would short-circuit every save. localStorage write on blur
    // is sub-millisecond; just always persist.
    setSavingKey(key);
    const next = saveDossier(playerId, { [key]: value });
    setNotes(next);
    setSavingKey(null);
    setSavedKey(key);
    window.setTimeout(() => setSavedKey(null), 1200);
  };

  return (
    <Calibrated min="GRASSROOTS">
      <Card variant="opaque" className="player-dossier">
        <div className="player-dossier__head">
          <div>
            <div className="page-section-label">Multidisciplinary review</div>
            <p className="player-dossier__sub">
              Per-corner notes on {playerName}. Saves as you type (on-device).
            </p>
          </div>
          <GlassPill mono>{pct}% complete</GlassPill>
        </div>

        <div className="player-dossier__grid">
          {CORNERS.map((c) => (
            <div key={c.key} className="player-dossier__field" data-corner={c.tag}>
              <div className="player-dossier__field-head">
                <span className="player-dossier__label">
                  <span className="player-dossier__dot" data-corner={c.tag} />
                  {c.label}
                </span>
                {notes[c.key].trim().length > 0 && (
                  <SpeakButton
                    text={notes[c.key]}
                    ariaLabel={`Read ${c.label} notes aloud`}
                    chunked
                  />
                )}
              </div>
              <textarea
                className="player-dossier__textarea"
                rows={4}
                value={notes[c.key]}
                onChange={(e) => setNotes({ ...notes, [c.key]: e.target.value })}
                onBlur={(e) => handleBlur(c.key, e.target.value)}
                placeholder={c.placeholder}
              />
              <span
                className="player-dossier__status"
                data-state={
                  savingKey === c.key ? 'saving' : savedKey === c.key ? 'saved' : undefined
                }
              >
                {savingKey === c.key ? 'saving…' : savedKey === c.key ? '✓ saved' : ''}
              </span>
            </div>
          ))}
        </div>

        <div className="player-dossier__foot">
          <Button
            variant="glass"
            size="touchline"
            onClick={() => {
              const cleared: DossierNotes = {
                technical_tactical: '',
                physical: '',
                psychological: '',
                social: '',
                updated_at: '',
              };
              saveDossier(playerId, cleared);
              setNotes(cleared);
            }}
          >
            Clear all
          </Button>
          {notes.updated_at && (
            <span className="player-dossier__updated">
              Last updated · {new Date(notes.updated_at).toLocaleString()}
            </span>
          )}
        </div>
      </Card>
    </Calibrated>
  );
}
